import { useState, useEffect } from 'react';
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Copy, Check, Circle, GripVertical, Trash2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type {
  VacancyDetail,
  VacancyPublicationStatus,
  ApplicationFormField,
  ApplicationFormSchema,
  ApplicationFieldType,
} from '@/types/recruitment';
import { useUpdateVacancy } from '@/hooks/use-recruitment';

// ── Field type config ────────────────────────────────────────

const FIELD_TYPES: { value: ApplicationFieldType; label: string }[] = [
  { value: 'SHORT_TEXT', label: 'Short text' },
  { value: 'LONG_TEXT', label: 'Long text' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'PHONE', label: 'Phone' },
  { value: 'URL', label: 'URL' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DROPDOWN', label: 'Dropdown' },
  { value: 'CHECKBOX', label: 'Checkbox' },
  { value: 'FILE', label: 'File upload' },
];

const MAX_CUSTOM_FIELDS = 10;

function fieldTypeLabel(type: ApplicationFieldType): string {
  return FIELD_TYPES.find((t) => t.value === type)?.label ?? type;
}

function generateFieldKey(): string {
  const id = (crypto.randomUUID?.() ?? Math.random().toString(36).slice(2)).replace(/-/g, '').slice(0, 8);
  return `field_${id}`;
}

// ── Publication status helpers ────────────────────────────────

const PUBLICATION_STATUSES: { value: VacancyPublicationStatus; label: string }[] = [
  { value: 'PUBLISHED', label: 'Published' },
  { value: 'HIDDEN', label: 'Hidden' },
  { value: 'UNPUBLISHED', label: 'Unpublished' },
];

function publicationIndicator(status: VacancyPublicationStatus) {
  switch (status) {
    case 'PUBLISHED':
      return { color: 'text-emerald-500', text: 'Live on site' };
    case 'HIDDEN':
      return { color: 'text-amber-500', text: 'Hidden — direct link only' };
    case 'UNPUBLISHED':
      return { color: 'text-red-500', text: 'Not published' };
  }
}

function checkPrerequisites(vacancy: VacancyDetail): string[] {
  const errors: string[] = [];
  if (vacancy.status !== 'ACTIVE') errors.push('Vacancy status must be Active');
  if (!vacancy.descriptionHtml?.trim()) errors.push('Job description is empty');
  if (!vacancy.pipelineStages || vacancy.pipelineStages.length < 3)
    errors.push('Pipeline must have at least 3 stages');
  if (!vacancy.members || vacancy.members.filter((m) => m.role === 'RECRUITER').length === 0)
    errors.push('At least 1 recruiter must be assigned');
  return errors;
}

// ── Schema validation (client) ───────────────────────────────

type FieldErrorCode =
  | 'LABEL_EMPTY'
  | 'DUPLICATE_KEY'
  | 'DROPDOWN_TOO_FEW_OPTIONS'
  | 'DROPDOWN_EMPTY_OPTION';

interface FieldValidationError {
  fieldKey: string;
  code: FieldErrorCode;
  message: string;
  /**
   * Which option box the message belongs under. Set for DROPDOWN_EMPTY_OPTION,
   * the only error whose input cannot be found from the field alone — a field
   * has one label, but any number of options.
   */
  optionIndex?: number;
}

/**
 * Every message the form has to say about the schema. Each one carries the field
 * — and for an option, the box — it belongs under, because that is the only
 * place it is rendered: a message with nowhere to sit would leave Save disabled
 * with nothing on screen explaining why.
 */
function validateSchema(fields: ApplicationFormField[]): FieldValidationError[] {
  const errors: FieldValidationError[] = [];
  const keys = new Set<string>();

  fields.forEach((f) => {
    if (!f.label.trim()) {
      errors.push({ fieldKey: f.key, code: 'LABEL_EMPTY', message: 'Label is required' });
    }
    if (keys.has(f.key)) {
      errors.push({ fieldKey: f.key, code: 'DUPLICATE_KEY', message: 'Duplicate field' });
    }
    keys.add(f.key);

    if (f.type === 'DROPDOWN') {
      const options = f.options ?? [];
      if (options.length < 2) {
        errors.push({
          fieldKey: f.key,
          code: 'DROPDOWN_TOO_FEW_OPTIONS',
          message: 'Dropdown must have at least 2 options',
        });
      } else {
        // One message per empty box rather than one for the field: with five
        // options, "an option is empty" is only actionable if it is the empty
        // one that turns red.
        options.forEach((option, optionIndex) => {
          if (!option.trim()) {
            errors.push({
              fieldKey: f.key,
              code: 'DROPDOWN_EMPTY_OPTION',
              message: 'Option cannot be empty',
              optionIndex,
            });
          }
        });
      }
    }
  });

  return errors;
}

// ── Sortable field row ───────────────────────────────────────

interface SortableFieldRowProps {
  field: ApplicationFormField;
  /** This field's own messages, already filtered by the parent. */
  errors: FieldValidationError[];
  onChange: (key: string, patch: Partial<ApplicationFormField>) => void;
  onDelete: (key: string) => void;
}

function SortableFieldRow({ field, errors, onChange, onDelete }: SortableFieldRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: field.key,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const isSystem = !!field.isSystem;
  const isDropdown = field.type === 'DROPDOWN';

  // Both label-level codes point at the same input, so whichever is present is
  // the one it shows; the option count sits under the options group, and an
  // empty option under its own box.
  const labelMessage = errors.find(
    (e) => e.code === 'LABEL_EMPTY' || e.code === 'DUPLICATE_KEY',
  )?.message;
  const optionCountMessage = errors.find(
    (e) => e.code === 'DROPDOWN_TOO_FEW_OPTIONS',
  )?.message;
  const optionMessages = new Map(
    errors
      .filter((e) => e.code === 'DROPDOWN_EMPTY_OPTION' && e.optionIndex !== undefined)
      .map((e) => [e.optionIndex, e.message] as const),
  );

  const errorIdBase = `application-field-${field.key}`;
  const labelErrorId = `${errorIdBase}-label-error`;
  const optionCountErrorId = `${errorIdBase}-options-error`;

  const handleAddOption = () => {
    onChange(field.key, { options: [...(field.options ?? []), ''] });
  };

  const handleOptionChange = (idx: number, value: string) => {
    const next = [...(field.options ?? [])];
    next[idx] = value;
    onChange(field.key, { options: next });
  };

  const handleRemoveOption = (idx: number) => {
    const next = (field.options ?? []).filter((_, i) => i !== idx);
    onChange(field.key, { options: next });
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'rounded-lg border bg-card',
        isDragging && 'opacity-50 shadow-lg',
        isSystem && 'bg-muted/30',
      )}
    >
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Drag handle */}
        <div
          className="flex-shrink-0 cursor-grab active:cursor-grabbing"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>

        {/* Label input */}
        <div className="flex-1 min-w-0">
          <Input
            value={field.label}
            onChange={(e) => onChange(field.key, { label: e.target.value })}
            placeholder="Field label"
            className={cn(
              'h-8 text-sm w-full',
              labelMessage && 'border-destructive focus-visible:ring-destructive',
            )}
            maxLength={120}
            aria-invalid={!!labelMessage || undefined}
            aria-describedby={labelMessage ? labelErrorId : undefined}
          />
        </div>

        {/* Type select / badge */}
        {isSystem ? (
          <Badge variant="outline" className="text-[11px] flex-shrink-0 w-[120px] justify-center">
            {fieldTypeLabel(field.type)}
          </Badge>
        ) : (
          <Select
            value={field.type}
            onValueChange={(v) => {
              const next: Partial<ApplicationFormField> = { type: v as ApplicationFieldType };
              if (v === 'DROPDOWN' && !field.options) next.options = ['', ''];
              if (v !== 'DROPDOWN') next.options = undefined;
              onChange(field.key, next);
            }}
          >
            <SelectTrigger className="h-8 w-[140px] text-xs flex-shrink-0">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FIELD_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Active toggle */}
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
          <Checkbox
            checked={field.active}
            disabled={isSystem && (field.semanticType === 'FULL_NAME' || field.semanticType === 'EMAIL')}
            onCheckedChange={(c) =>
              onChange(field.key, {
                active: c === true,
                required: c === true ? field.required : false,
              })
            }
          />
          Active
        </label>

        {/* Required toggle */}
        <label className="flex items-center gap-1.5 text-xs text-muted-foreground flex-shrink-0">
          <Checkbox
            checked={field.required}
            disabled={
              !field.active ||
              (isSystem && (field.semanticType === 'FULL_NAME' || field.semanticType === 'EMAIL'))
            }
            onCheckedChange={(c) => onChange(field.key, { required: c === true })}
          />
          Required
        </label>

        {/* Delete */}
        {!isSystem && (
          <button
            type="button"
            onClick={() => onDelete(field.key)}
            className="flex-shrink-0 p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
            title="Delete field"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Below the control line rather than inside it: the line is
          `items-center`, so a message stacked under the label input would grow
          that column and re-centre the type select, the toggles and the delete
          button against the rest of the list. Indented to sit under the input
          it belongs to — `px-3` of the line plus the drag handle and its gap. */}
      {labelMessage && (
        <p
          id={labelErrorId}
          role="alert"
          className="pl-9 pr-3 pb-2 -mt-1.5 text-[11px] text-destructive"
        >
          {labelMessage}
        </p>
      )}

      {/* DROPDOWN options panel — always visible */}
      {isDropdown && (
        <div
          className="border-t px-3 py-2.5 space-y-2"
          role="group"
          aria-label="Options"
          // The count rule belongs to the group, so the group is what points at
          // it: `role="alert"` announces it once, and without this a reader
          // moving between the option boxes afterwards has no way back to it.
          aria-describedby={optionCountMessage ? optionCountErrorId : undefined}
        >
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground font-medium">
            Options
          </div>
          <div className="space-y-1.5">
            {(field.options ?? []).map((opt, idx) => {
              const optionMessage = optionMessages.get(idx);
              const optionErrorId = `${errorIdBase}-option-${idx}-error`;
              return (
                <div key={idx} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => handleOptionChange(idx, e.target.value)}
                      placeholder={`Option ${idx + 1}`}
                      className={cn(
                        'h-7 text-xs',
                        optionMessage && 'border-destructive focus-visible:ring-destructive',
                      )}
                      autoFocus={idx === 0 && opt === ''}
                      aria-invalid={!!optionMessage || undefined}
                      aria-describedby={optionMessage ? optionErrorId : undefined}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(idx)}
                      disabled={(field.options?.length ?? 0) <= 2}
                      className={cn(
                        'p-1 rounded text-muted-foreground hover:text-destructive transition-colors',
                        (field.options?.length ?? 0) <= 2 && 'opacity-30 cursor-not-allowed',
                      )}
                    >
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                  {optionMessage && (
                    <p id={optionErrorId} role="alert" className="text-[11px] text-destructive">
                      {optionMessage}
                    </p>
                  )}
                </div>
              );
            })}
            <button
              type="button"
              onClick={handleAddOption}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <Plus className="h-3 w-3" />
              Add option
            </button>
            {/* Belongs to the group rather than to any one box: it is the number
                of options that is wrong, not a value inside them. */}
            {optionCountMessage && (
              <p id={optionCountErrorId} role="alert" className="text-[11px] text-destructive">
                {optionCountMessage}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ───────────────────────────────────────────

interface VacancyApplicationFormTabProps {
  vacancy: VacancyDetail;
}

function reindexPositions(fields: ApplicationFormField[]): ApplicationFormField[] {
  return fields.map((f, i) => ({ ...f, position: i }));
}

export function VacancyApplicationFormTab({ vacancy }: VacancyApplicationFormTabProps) {
  const initialFields = vacancy.applicationForm?.fields ?? [];
  const [fields, setFields] = useState<ApplicationFormField[]>(initialFields);
  const [pubStatus, setPubStatus] = useState<VacancyPublicationStatus>(vacancy.publicationStatus);
  const [copied, setCopied] = useState(false);
  const [prereqErrors, setPrereqErrors] = useState<string[]>([]);

  const updateVacancy = useUpdateVacancy();

  useEffect(() => {
    setFields(vacancy.applicationForm?.fields ?? []);
    setPubStatus(vacancy.publicationStatus);
  }, [vacancy.applicationForm, vacancy.publicationStatus]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const validationErrors = validateSchema(fields);
  const customCount = fields.filter((f) => !f.isSystem).length;

  const isDirty =
    JSON.stringify(fields) !== JSON.stringify(initialFields) ||
    pubStatus !== vacancy.publicationStatus;

  const handleFieldChange = (key: string, patch: Partial<ApplicationFormField>) => {
    setFields((prev) => prev.map((f) => (f.key === key ? { ...f, ...patch } : f)));
  };

  const handleFieldDelete = (key: string) => {
    setFields((prev) => reindexPositions(prev.filter((f) => f.key !== key)));
  };

  const handleAddField = () => {
    const newField: ApplicationFormField = {
      key: generateFieldKey(),
      label: 'Untitled field',
      type: 'SHORT_TEXT',
      active: true,
      required: false,
      position: fields.length,
    };
    setFields((prev) => [...prev, newField]);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = fields.findIndex((f) => f.key === active.id);
    const newIndex = fields.findIndex((f) => f.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setFields((prev) => reindexPositions(arrayMove(prev, oldIndex, newIndex)));
  };

  const handlePublicationChange = (value: VacancyPublicationStatus) => {
    if (value !== 'UNPUBLISHED') {
      const errors = checkPrerequisites(vacancy);
      if (errors.length > 0) {
        setPrereqErrors(errors);
        return;
      }
    }
    setPrereqErrors([]);
    setPubStatus(value);
  };

  const handleSave = () => {
    const formSchema: ApplicationFormSchema = { fields };
    updateVacancy.mutate({
      id: vacancy.id,
      applicationForm: formSchema,
      publicationStatus: pubStatus,
    });
  };

  const handleCopyUrl = async () => {
    if (!vacancy.publicSlug) return;
    const url = `${window.location.origin}/careers/${vacancy.publicSlug}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const indicator = publicationIndicator(vacancy.publicationStatus);
  const pubStatusUnsaved = pubStatus !== vacancy.publicationStatus;

  return (
    <div className="space-y-8">
      {/* Publication */}
      <div className="space-y-4">
        <h3 className="text-sm font-medium">Publication</h3>

        <div className="flex items-center gap-4">
          <div className="space-y-1">
            <label className="text-sm text-muted-foreground">Status</label>
            <Select value={pubStatus} onValueChange={handlePublicationChange}>
              <SelectTrigger className="h-9 w-[200px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PUBLICATION_STATUSES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2 pt-5">
            <Circle className={cn('h-2.5 w-2.5 fill-current', indicator.color)} />
            <span className={cn('text-sm', indicator.color)}>{indicator.text}</span>
            {pubStatusUnsaved && (
              <span className="text-xs text-muted-foreground">
                Unsaved — click Save to apply
              </span>
            )}
          </div>
        </div>

        {prereqErrors.length > 0 && (
          <div className="rounded-md border border-destructive/50 bg-destructive/5 p-3 text-sm">
            <p className="font-medium text-destructive mb-1">Cannot publish. Fix the following:</p>
            <ul className="list-disc list-inside text-destructive/80 space-y-0.5">
              {prereqErrors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {vacancy.publicSlug &&
          vacancy.publicationStatus !== 'UNPUBLISHED' &&
          vacancy.status === 'ACTIVE' && (
            <div className="space-y-1">
              <label className="text-sm text-muted-foreground">Public URL</label>
              <div className="flex items-center gap-2">
                <code className="text-sm bg-muted px-3 py-1.5 rounded-md flex-1 truncate">
                  {window.location.origin}/careers/{vacancy.publicSlug}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopyUrl}>
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          )}
      </div>

      {/* Form Fields */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Application Form Fields</h3>
          <span className="text-xs text-muted-foreground">
            {customCount} / {MAX_CUSTOM_FIELDS} custom fields
          </span>
        </div>

        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={fields.map((f) => f.key)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {fields.map((field) => (
                <SortableFieldRow
                  key={field.key}
                  field={field}
                  errors={validationErrors.filter((e) => e.fieldKey === field.key)}
                  onChange={handleFieldChange}
                  onDelete={handleFieldDelete}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>

        <Button
          variant="outline"
          size="sm"
          onClick={handleAddField}
          disabled={customCount >= MAX_CUSTOM_FIELDS}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add field
        </Button>
      </div>

      {/* Save */}
      <div className="flex justify-end">
        <Button
          variant="accent"
          onClick={handleSave}
          disabled={!isDirty || validationErrors.length > 0 || updateVacancy.isPending}
        >
          {updateVacancy.isPending ? 'Saving...' : 'Save'}
        </Button>
      </div>
    </div>
  );
}
